import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { collection, onSnapshot, query, where, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import { useUser } from './UserContext';

// Create context
const AdminDataContext = createContext();

// Action types
const ADMIN_DATA_ACTIONS = {
  SET_LOADING: 'SET_LOADING',
  SET_ENROLMENTS: 'SET_ENROLMENTS',
  SET_CHILDREN: 'SET_CHILDREN',
  SET_SESSIONS: 'SET_SESSIONS',
  SET_EMAIL_LOGS: 'SET_EMAIL_LOGS',
  SET_USERS: 'SET_USERS',
  SET_ERROR: 'SET_ERROR',
  CLEAR_ERROR: 'CLEAR_ERROR',
  UPDATE_CACHE_TIMESTAMP: 'UPDATE_CACHE_TIMESTAMP'
};

// Initial state
const initialState = {
  enrolments: [],
  children: [],
  sessions: [],
  emailLogs: [],
  users: [],
  loading: {
    enrolments: false,
    children: false,
    sessions: false,
    emailLogs: false,
    users: false
  },
  errors: {},
  lastUpdated: {
    enrolments: null,
    children: null,
    sessions: null,
    emailLogs: null,
    users: null
  },
  cacheTimestamp: Date.now()
};

// Reducer
function adminDataReducer(state, action) {
  switch (action.type) {
    case ADMIN_DATA_ACTIONS.SET_LOADING:
      return {
        ...state,
        loading: {
          ...state.loading,
          [action.payload.collection]: action.payload.isLoading
        }
      };

    case ADMIN_DATA_ACTIONS.SET_ENROLMENTS:
      return {
        ...state,
        enrolments: action.payload,
        loading: { ...state.loading, enrolments: false },
        lastUpdated: { ...state.lastUpdated, enrolments: Date.now() },
        errors: { ...state.errors, enrolments: null }
      };

    case ADMIN_DATA_ACTIONS.SET_CHILDREN:
      return {
        ...state,
        children: action.payload,
        loading: { ...state.loading, children: false },
        lastUpdated: { ...state.lastUpdated, children: Date.now() },
        errors: { ...state.errors, children: null }
      };

    case ADMIN_DATA_ACTIONS.SET_SESSIONS:
      return {
        ...state,
        sessions: action.payload,
        loading: { ...state.loading, sessions: false },
        lastUpdated: { ...state.lastUpdated, sessions: Date.now() },
        errors: { ...state.errors, sessions: null }
      };

    case ADMIN_DATA_ACTIONS.SET_EMAIL_LOGS:
      return {
        ...state,
        emailLogs: action.payload,
        loading: { ...state.loading, emailLogs: false },
        lastUpdated: { ...state.lastUpdated, emailLogs: Date.now() },
        errors: { ...state.errors, emailLogs: null }
      };

    case ADMIN_DATA_ACTIONS.SET_USERS:
      return {
        ...state,
        users: action.payload,
        loading: { ...state.loading, users: false },
        lastUpdated: { ...state.lastUpdated, users: Date.now() },
        errors: { ...state.errors, users: null }
      };

    case ADMIN_DATA_ACTIONS.SET_ERROR:
      return {
        ...state,
        errors: {
          ...state.errors,
          [action.payload.collection]: action.payload.error
        },
        loading: {
          ...state.loading,
          [action.payload.collection]: false
        }
      };

    case ADMIN_DATA_ACTIONS.CLEAR_ERROR:
      return {
        ...state,
        errors: {
          ...state.errors,
          [action.payload.collection]: null
        }
      };

    case ADMIN_DATA_ACTIONS.UPDATE_CACHE_TIMESTAMP:
      return {
        ...state,
        cacheTimestamp: Date.now()
      };

    default:
      return state;
  }
}

// Provider component
export const AdminDataProvider = ({ children }) => {
  const [state, dispatch] = useReducer(adminDataReducer, initialState);
  const { userData } = useUser();
  const userRole = userData?.role;
  const parishId = userData?.parishId;

  // Helper function to handle errors
  const handleError = (collection, error) => {
    console.error(`Error loading ${collection}:`, error);
    dispatch({
      type: ADMIN_DATA_ACTIONS.SET_ERROR,
      payload: { collection, error: error.message }
    });
  };

  // Set up real-time listeners for admin data
  useEffect(() => {
    console.log('🔍 AdminDataContext useEffect - userData:', userData);
    console.log('🔍 AdminDataContext useEffect - userRole:', userRole);
    console.log('🔍 AdminDataContext useEffect - parishId:', parishId);
    
    if (!userData || !parishId || !['parishAdmin', 'globalAdmin', 'developer', 'assistant'].includes(userRole)) {
      console.log('🚫 AdminDataContext - Not setting up listeners. Missing data or insufficient role.');
      return;
    }

    const unsubscribers = [];

    console.log('🔄 Setting up real-time admin data listeners...');

    // 1. Enrolments listener
    try {
      dispatch({
        type: ADMIN_DATA_ACTIONS.SET_LOADING,
        payload: { collection: 'enrolments', isLoading: true }
      });

      const enrolmentsQuery = query(
        collection(db, 'enrolments'),
        // Temporarily remove parishId filter for developers
        ...(userRole === 'developer' ? [] : [where('parishId', '==', parishId)]),
        orderBy('createdAt', 'desc')
      );

      const unsubEnrolments = onSnapshot(
        enrolmentsQuery,
        (snapshot) => {
          const enrolmentsData = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          console.log(`✅ Enrolments updated: ${enrolmentsData.length} records`);
          console.log('📊 First few enrolments:', enrolmentsData.slice(0, 3));
          dispatch({
            type: ADMIN_DATA_ACTIONS.SET_ENROLMENTS,
            payload: enrolmentsData
          });
        },
        (error) => handleError('enrolments', error)
      );
      unsubscribers.push(unsubEnrolments);
    } catch (error) {
      handleError('enrolments', error);
    }

    // 2. Children listener
    try {
      dispatch({
        type: ADMIN_DATA_ACTIONS.SET_LOADING,
        payload: { collection: 'children', isLoading: true }
      });

      const childrenQuery = query(
        collection(db, 'children'),
        orderBy('createdAt', 'desc')
      );

      const unsubChildren = onSnapshot(
        childrenQuery,
        (snapshot) => {
          const childrenData = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          console.log(`✅ Children updated: ${childrenData.length} records`);
          dispatch({
            type: ADMIN_DATA_ACTIONS.SET_CHILDREN,
            payload: childrenData
          });
        },
        (error) => handleError('children', error)
      );
      unsubscribers.push(unsubChildren);
    } catch (error) {
      handleError('children', error);
    }

    // 3. Sessions listener
    try {
      dispatch({
        type: ADMIN_DATA_ACTIONS.SET_LOADING,
        payload: { collection: 'sessions', isLoading: true }
      });

      const sessionsQuery = query(
        collection(db, 'sessions'),
        // Temporarily remove parishId filter for developers
        ...(userRole === 'developer' ? [] : [where('parishId', '==', parishId)]),
        orderBy('sessionAt', 'asc')
      );

      const unsubSessions = onSnapshot(
        sessionsQuery,
        (snapshot) => {
          const sessionsData = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          console.log(`✅ Sessions updated: ${sessionsData.length} records`);
          dispatch({
            type: ADMIN_DATA_ACTIONS.SET_SESSIONS,
            payload: sessionsData
          });
        },
        (error) => handleError('sessions', error)
      );
      unsubscribers.push(unsubSessions);
    } catch (error) {
      handleError('sessions', error);
    }

    // 4. Email Logs listener (only for parish admins and above)
    if (['parishAdmin', 'globalAdmin', 'developer'].includes(userRole)) {
      try {
        dispatch({
          type: ADMIN_DATA_ACTIONS.SET_LOADING,
          payload: { collection: 'emailLogs', isLoading: true }
        });

        const emailLogsQuery = query(
          collection(db, 'emailLog'),
          orderBy('createdAt', 'desc')
        );

        const unsubEmailLogs = onSnapshot(
          emailLogsQuery,
          (snapshot) => {
            const emailLogsData = snapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data()
            }));
            console.log(`✅ Email logs updated: ${emailLogsData.length} records`);
            dispatch({
              type: ADMIN_DATA_ACTIONS.SET_EMAIL_LOGS,
              payload: emailLogsData
            });
          },
          (error) => handleError('emailLogs', error)
        );
        unsubscribers.push(unsubEmailLogs);
      } catch (error) {
        handleError('emailLogs', error);
      }
    }

    // 5. Users listener (only for parish admins and above)
    if (['parishAdmin', 'globalAdmin', 'developer'].includes(userRole)) {
      try {
        dispatch({
          type: ADMIN_DATA_ACTIONS.SET_LOADING,
          payload: { collection: 'users', isLoading: true }
        });

        const usersQuery = query(
          collection(db, 'users'),
          // Temporarily remove parishId filter for developers
          ...(userRole === 'developer' ? [] : [where('parishId', '==', parishId)]),
          orderBy('createdAt', 'desc')
        );

        const unsubUsers = onSnapshot(
          usersQuery,
          (snapshot) => {
            const usersData = snapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data()
            }));
            console.log(`✅ Users updated: ${usersData.length} records`);
            dispatch({
              type: ADMIN_DATA_ACTIONS.SET_USERS,
              payload: usersData
            });
          },
          (error) => handleError('users', error)
        );
        unsubscribers.push(unsubUsers);
      } catch (error) {
        handleError('users', error);
      }
    }

    // Cleanup function
    return () => {
      console.log('🧹 Cleaning up admin data listeners...');
      unsubscribers.forEach(unsubscribe => unsubscribe());
    };
  }, [userData, userRole, parishId]);

  // Browser cache persistence (optional enhancement)
  useEffect(() => {
    const cacheKey = `adminData_${parishId}_${userRole}`;
    
    // Load from cache on mount
    const cachedData = localStorage.getItem(cacheKey);
    if (cachedData) {
      try {
        const parsed = JSON.parse(cachedData);
        // Only use cache if it's less than 5 minutes old
        if (Date.now() - parsed.timestamp < 5 * 60 * 1000) {
          console.log('📦 Loading admin data from browser cache');
          // Note: We still set up listeners for real-time updates
        }
      } catch (error) {
        console.warn('Failed to parse cached admin data:', error);
        localStorage.removeItem(cacheKey);
      }
    }

    // Save to cache when data changes
    const saveToCache = () => {
      try {
        const dataToCache = {
          enrolments: state.enrolments,
          children: state.children,
          sessions: state.sessions,
          emailLogs: state.emailLogs,
          users: state.users,
          timestamp: Date.now()
        };
        localStorage.setItem(cacheKey, JSON.stringify(dataToCache));
      } catch (error) {
        console.warn('Failed to cache admin data:', error);
      }
    };

    // Debounce cache saves
    const cacheTimer = setTimeout(saveToCache, 1000);
    return () => clearTimeout(cacheTimer);
  }, [state, parishId, userRole]);

  // Context value
  const value = {
    ...state,
    dispatch,
    // Helper functions
    clearError: (collection) => {
      dispatch({
        type: ADMIN_DATA_ACTIONS.CLEAR_ERROR,
        payload: { collection }
      });
    },
    refreshData: () => {
      dispatch({ type: ADMIN_DATA_ACTIONS.UPDATE_CACHE_TIMESTAMP });
    },
    // Data filtering helpers
    getEnrolmentsByStatus: (status) => {
      return state.enrolments.filter(enrolment => enrolment.status === status);
    },
    getEnrolmentsBySacrament: (sacrament) => {
      return state.enrolments.filter(enrolment => enrolment.sacrament === sacrament);
    },
    getSessionsBySacrament: (sacrament) => {
      return state.sessions.filter(session => session.sacrament === sacrament);
    },
    getChildById: (childId) => {
      return state.children.find(child => child.id === childId);
    },
    getUserById: (userId) => {
      return state.users.find(user => user.id === userId);
    }
  };

  return (
    <AdminDataContext.Provider value={value}>
      {children}
    </AdminDataContext.Provider>
  );
};

// Custom hook to use admin data
export const useAdminData = () => {
  const context = useContext(AdminDataContext);
  if (!context) {
    throw new Error('useAdminData must be used within AdminDataProvider');
  }
  return context;
};

export default AdminDataContext;
